// B-4: the UPCOMING card variant + "Remind me".
//
// Same geometry as the live card (StreamCard shell + StreamCardBody),
// differing only in: grayscale-muted thumbnail (SOR-622 — kills the colour
// of creator-burned LIVE badges; the accent-blue countdown badge is the
// only saturated element on the card), relative-countdown badge instead
// of LIVE, no watch count, local start time in the meta row's right slot,
// and one secondary "Remind me" action — the only button on any card in
// the grid. The grayscale filter + white veil live in index.css
// (.upcoming-thumb / .upcoming-veil) so forced-colors mode can drop them.
//
// Reminder state is prop-driven (`reminded` + `onToggleReminder`) — no
// internal state. D-3 (persistence) slots in behind this interface.
// The button stops propagation AND prevents default so it never triggers
// the card's link navigation.
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React from 'react';
import posthog from 'posthog-js';
import StreamCard from './StreamCard';
import StreamCardBody from './StreamCardBody';
import { BellIcon, CalendarIcon } from './icons';
import { formatCountdown, formatLocalDate, formatLocalTime } from './format';

// SOR-625: below sm the card is a row (StreamCard mobileRow) and the
// reminder control is a 44x44 bell icon button absolutely centered on the
// row's trailing edge (the body reserves pr-11 for it); at sm: it restores
// the full-width bordered pill under the meta row.
const BTN_BASE =
  'absolute right-1.5 top-1/2 flex h-11 w-11 translate-y-[-50%] items-center justify-center rounded-btn font-inter-tight text-xs font-medium leading-none transition duration-180 ease-[ease] sm:static sm:h-[30px] sm:w-full sm:translate-y-[0px] sm:gap-1.5 sm:border';
const BTN_IDLE =
  'border-browse-border-strong text-browse-text-button hover:border-browse-accent hover:text-browse-accent';
const BTN_SET = 'border-browse-accent text-browse-accent';

/**
 * @param {Object} props
 * @param {import('./stream').Stream} props.stream  an upcoming stream
 * @param {boolean} props.reminded  reminder set for this stream (prop-driven)
 * @param {(streamId: number) => void} props.onToggleReminder
 * @param {number} [props.now]  epoch ms; pass to pin the clock in tests
 * @param {string} [props.timeZone]  override for tests; defaults to the viewer's zone
 */
const UpcomingCard = ({ stream, reminded, onToggleReminder, now, timeZone, dated = false }) => {
  const countdown = formatCountdown(stream.startsAt, now);
  // Far-out streams (the "Coming up" bucket) show a dated start — a bare
  // "3:00 PM" is misleading for a stream two weeks away.
  const startLabel = dated
    ? [formatLocalDate(stream.startsAt, timeZone), formatLocalTime(stream.startsAt, timeZone)]
        .filter(Boolean)
        .join(' · ')
    : formatLocalTime(stream.startsAt, timeZone);

  const handleRemind = (e) => {
    e.preventDefault(); // never trigger the card link's navigation
    e.stopPropagation();
    onToggleReminder(stream.id);
  };

  const handleCardClick = () => {
    posthog.capture('stream_clicked', {
      stream_id: stream.id,
      stream_status: 'upcoming',
    });
  };

  return (
    <StreamCard
      mobileRow
      watchUrl={stream.watchUrl}
      onClick={handleCardClick}
      thumbnail={
        <>
          {stream.thumbnailUrl ? (
            <>
              <img src={stream.thumbnailUrl} alt="" className="upcoming-thumb" />
              <span
                aria-hidden="true"
                className="upcoming-veil pointer-events-none absolute inset-0 bg-white/[.08]"
              />
            </>
          ) : (
            <span aria-hidden="true" className="block h-full w-full bg-browse-placeholder-grey" />
          )}
          {countdown != null && (
            // 18px tall on the 120px mobile thumb, 22px in the card grid
            <span className="absolute left-1.5 top-1.5 flex h-[18px] items-center rounded-badge bg-browse-accent px-1.75 font-inter-tight text-10 font-semibold leading-none tracking-badge-upcoming text-white shadow-badge-upcoming sm:left-2.25 sm:top-2.25 sm:h-[22px] sm:px-2">
              {countdown}
            </span>
          )}
        </>
      }
    >
      <StreamCardBody
        title={stream.title}
        channelName={stream.channelName}
        channelAvatarUrl={stream.channelAvatarUrl}
        metaRight={startLabel}
      />
      <button
        type="button"
        onClick={handleRemind}
        aria-label={reminded ? 'Reminder set' : 'Remind me'}
        className={`${BTN_BASE} ${reminded ? BTN_SET : BTN_IDLE}`}
      >
        <BellIcon aria-hidden="true" className="sm:hidden" />
        <span className="hidden items-center gap-1.5 sm:flex">
          <CalendarIcon />
          {reminded ? 'Reminder set' : 'Remind me'}
        </span>
      </button>
    </StreamCard>
  );
};

export default UpcomingCard;
